import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { api } from '../lib/api';

const Ctx = createContext(null);

/**
 * Publishes the state of the external booking register:
 *  - status (healthy/stale/last_read_at)
 *  - a refresh() function
 *  - freshness (seconds since last successful read), updated locally every second
 *  - isStale — writes should be disabled when true
 */
export function RegisterStatusProvider({ children }) {
  const [status, setStatus] = useState(null);
  const [freshness, setFreshness] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const lastReadAtRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/register/status');
      setStatus(data);
      lastReadAtRef.current = data.last_read_at ? new Date(data.last_read_at).getTime() : null;
      setFreshness(data.seconds_since_read);
    } catch {
      // ignore transient
    }
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post('/register/refresh');
      setStatus(data);
      lastReadAtRef.current = data.last_read_at ? new Date(data.last_read_at).getTime() : null;
      setFreshness(data.seconds_since_read);
    } catch {
      // status polling will surface the error
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll status every 15s (register itself has 30s cache TTL)
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  // Locally tick freshness once a second
  useEffect(() => {
    const t = setInterval(() => {
      if (lastReadAtRef.current) {
        setFreshness(Math.max(0, Math.floor((Date.now() - lastReadAtRef.current) / 1000)));
      }
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const value = { status, freshness, refresh, refreshing, isStale: !!status?.stale };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useRegisterStatus = () => useContext(Ctx);

export function humanFreshness(seconds) {
  if (seconds == null) return 'never';
  if (seconds < 5) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m} min${m === 1 ? '' : 's'} ago`;
  const h = Math.floor(m / 60);
  return `${h} hour${h === 1 ? '' : 's'} ago`;
}
